import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { useIntl } from 'react-intl';
import { Tab } from '@ingka/tabs';
import { SelectedMenuProductList, Widget } from 'rangeConfig/@types';
import { useSelected } from 'contexts/selected';
import ProductThumbs from 'selectedMenu/ProductThumbs';
import isWidget from 'rangeConfig/shared/utils/isWidget';
import { useCore } from 'contexts/core';
import { getMenuTitle } from 'components/ProductSidebar/menus/utils/utils';
import { isMobile } from 'utils/platform';
import SkapaTabs from 'ui/Tabs/SkapaTabs';

const TabsWrapper = styled.div`
  padding: 0 ${({ theme }) => theme.space.xs};
  width: 100vw;
`;

const Content = styled.div`
  display: flex;
  height: 100%;
  width: 100%;
  align-items: center;
  justify-content: center;
`;

const Item = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;

  &[data-hidden='true'] {
    display: none;
  }
`;

const WidgetWrapper = styled.div`
  width: 100%;
`;

type Props = {
  content: SelectedMenuProductList[] | undefined;
};

const SelectedMenuContent = ({ content }: Props) => {
  const { formatMessage } = useIntl();
  const { getContentProducts, hiddenContent } = useSelected();
  const coreApi = useCore();

  const [selectedTab, setSelectedTab] = useState<string | null>(null);
  const [prevVisibleContent, setPrevVisibleContent] = useState<SelectedMenuProductList[]>([]);

  const contentShouldBeVisible = useCallback(
    (content: SelectedMenuProductList): boolean => {
      if (isWidget(content)) {
        return !!content.title && !hiddenContent(content);
      } else {
        const contentProducts = getContentProducts(content);
        return contentProducts.length > 0 && !hiddenContent(content);
      }
    },
    [getContentProducts, hiddenContent]
  );

  useEffect(() => {
    const visibleContent = content?.filter(item => contentShouldBeVisible(item)) || [];
    const selectedTabDoesNotExistInContent = (content: SelectedMenuProductList[]): boolean => {
      const selectedTabExists = content?.findIndex(
        item => item.id === selectedTab && contentShouldBeVisible(item)
      );
      return selectedTabExists <= 0;
    };

    if (visibleContent.length > 0) {
      if (visibleContent.filter(item => !prevVisibleContent.includes(item)).length > 0) {
        setSelectedTab(visibleContent[0].id);
        setPrevVisibleContent(visibleContent);
      } else if (selectedTabDoesNotExistInContent(visibleContent)) {
        setSelectedTab(visibleContent[0].id);
      }
    }
    // eslint-disable-next-line
  }, [content, prevVisibleContent, contentShouldBeVisible]);

  const shoudDisplayTabs = content?.some(c => c.title);

  return (
    <>
      {shoudDisplayTabs && (
        <TabsWrapper>
          {selectedTab && content && (
            <SkapaTabs
              defaultActiveTab={content[0].id}
              activeTab={selectedTab}
              onTabChanged={(tab: React.SetStateAction<string | null>) => setSelectedTab(tab)}
              tabs={content
                .filter(item => contentShouldBeVisible(item))
                .map(item => (
                  <Tab
                    key={item.id}
                    tabPanelId={item.id}
                    data-testid={`mobile-menu-tab-${item.id}`}
                    text={getMenuTitle(item.title, item.titleValue, formatMessage, coreApi)}
                    disabled={item.disabled && item.disabled(coreApi)}
                  />
                ))}
            />
          )}
        </TabsWrapper>
      )}
      <Content>
        <>
          {content?.map(item => {
            const { id, component: Component } = (item as any) as Widget;
            return (
              <Item key={item.id} data-hidden={selectedTab !== item.id}>
                {isWidget(item) ? (
                  <WidgetWrapper style={isMobile() ? { margin: 0 } : { margin: '0 0.5rem' }}>
                    <Component key={id} />
                  </WidgetWrapper>
                ) : (
                  <ProductThumbs content={item} />
                )}
              </Item>
            );
          })}
        </>
      </Content>
    </>
  );
};

export default SelectedMenuContent;
